/**
 * Pure helpers for deriving edit gallery lists from raw state.
 * Used by EngravingGenerator computed properties.
 */

/** Basename from image URL for nanoBanana delete API when API omits filename. */
export function nanoBananaFilenameFromImageUrl(url) {
  if (!url || typeof url !== 'string') return ''
  try {
    const u = new URL(url)
    const seg = u.pathname.split('/').filter(Boolean).pop()
    return seg ? decodeURIComponent(seg) : ''
  } catch {
    return ''
  }
}

export function dedupeByImageUrl(items) {
  const seen = new Set()
  const out = []
  for (const item of items) {
    if (!item || !item.imageUrl) continue
    if (seen.has(item.imageUrl)) continue
    seen.add(item.imageUrl)
    out.push(item)
  }
  return out
}

export function buildStep1GalleryItems(uploadedFromGallery, orderInputItems) {
  const byUrl = new Map()
  for (const item of [...(uploadedFromGallery || []), ...(orderInputItems || [])]) {
    if (!item || !item.imageUrl) continue
    if (!byUrl.has(item.imageUrl)) byUrl.set(item.imageUrl, { ...item, id: item.id || item.imageUrl })
  }
  return Array.from(byUrl.values())
}

export function buildEditsGalleryItems(edits) {
  return (edits || []).map((url, i) => ({
    id: `edit-${i}`,
    imageUrl: url,
    label: `Edit ${i + 1}`,
  }))
}

export function buildPreviouslyGeneratedEditsItems({ sessionEdits, edits, backendFilteredItems }) {
  const currentRunUrls = new Set(edits || [])
  const sessionUrls = new Set(sessionEdits || [])
  const previousSessionUrls = (sessionEdits || []).filter((url) => !currentRunUrls.has(url))
  const previousSessionItems = previousSessionUrls.map((url, i) => ({
    id: `prev-session-${i}`,
    imageUrl: url,
    label: `Edit ${i + 1}`,
  }))
  const backendOnly = (backendFilteredItems || []).filter((item) => !sessionUrls.has(item.imageUrl))
  const backendItems = backendOnly.map((item, i) => ({
    id: item.id || `prev-backend-${i}`,
    imageUrl: item.imageUrl,
    label: `Edit ${i + 1}`,
  }))
  return dedupeByImageUrl([...previousSessionItems, ...backendItems])
}

export function buildAllEditsItems(sessionEdits, previouslyGeneratedEditsItems) {
  const sessionItems = (sessionEdits || []).map((url, i) => ({
    id: `session-${i}`,
    imageUrl: url,
    label: `Edit ${i + 1}`,
  }))
  return dedupeByImageUrl([...sessionItems, ...(previouslyGeneratedEditsItems || [])])
}

export function buildAllEditsForMask(allEditsItems, maskUploadedFromGallery) {
  return dedupeByImageUrl([...(allEditsItems || []), ...(maskUploadedFromGallery || [])])
}

/**
 * Step 3 left gallery: AI-generated edits first, then user-uploaded images (step 1 / order) at the bottom.
 * Dedupes by imageUrl so uploads already present in edits are not repeated.
 */
export function buildStep3GalleryItems(allEditsForMask, userUploadedItems) {
  return dedupeByImageUrl([...(allEditsForMask || []), ...(userUploadedItems || [])])
}
